import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { REVIEW_STATUS_CHIP } from "./CyclesPanel";
import ReviewDialog from "./ReviewDialog";

export const CycleDetail = ({ cycleId, onBack }) => {
  const [cycle, setCycle] = useState(null);
  const [openReview, setOpenReview] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    const r = await api.get(`/reviews/cycles/${cycleId}`);
    setCycle(r.data);
    return r.data;
  };

  useEffect(() => {
    load();
    return () => clearInterval(timer.current);
  }, [cycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while a batch synthesis job is running
  useEffect(() => {
    const running = cycle?.synthesis_job?.status === "running";
    clearInterval(timer.current);
    if (running) timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [cycle?.synthesis_job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cycle) return <Skeleton className="h-96 w-full rounded-none" />;

  const job = cycle.synthesis_job;
  const jobRunning = job?.status === "running";
  const pendingCount = (cycle.review_counts || {}).pending || 0;

  const batchSynthesize = async () => {
    try {
      const r = await api.post(`/reviews/cycles/${cycleId}/synthesize-all`);
      if (r.data.queued === 0) toast.info(`Nothing to synthesize — ${r.data.skipped_already_drafted} already drafted or finalized`);
      else toast.success(`Batch started: ${r.data.queued} queued, ${r.data.skipped_already_drafted} skipped (already drafted)`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const synthesizeOne = async (review) => {
    setRowBusy(review.id);
    try {
      await api.post(`/reviews/${review.id}/synthesize`, {});
      toast.success(`AI draft ready for ${review.employee_name}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRowBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="cycle-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="cycle-back-btn"
            className="rounded-none h-8 text-[12px] text-muted-foreground -ml-2">
            <ArrowLeft className="h-4 w-4" /> Cycles
          </Button>
          <h2 className="text-lg font-bold text-[var(--brand-primary)]">{cycle.name}</h2>
          <span className="text-[11px] font-mono text-muted-foreground">{cycle.period_start} → {cycle.period_end}</span>
        </div>
        <Button onClick={batchSynthesize} disabled={jobRunning || pendingCount === 0} data-testid="synthesize-all-btn"
          className="rounded-none h-9 text-[12px] bg-teal-600 hover:bg-teal-700 disabled:opacity-50">
          <Sparkles className="h-4 w-4" />
          {jobRunning ? "Synthesizing…" : `Synthesize all pending (${pendingCount})`}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-border px-4 py-3" data-testid="cycle-feedback-progress">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Peer feedback</p>
          <p className="text-[16px] font-bold text-[var(--brand-primary)] mt-1 font-mono">
            {cycle.feedback.submitted}<span className="text-slate-400 font-normal"> / {cycle.feedback.total}</span>
          </p>
          <div className="h-1.5 bg-slate-100 mt-2">
            <div className="h-1.5 bg-teal-600"
              style={{ width: `${cycle.feedback.total ? (cycle.feedback.submitted / cycle.feedback.total) * 100 : 0}%` }} />
          </div>
        </div>
        {["pending", "ai_draft", "finalized"].map((s) => (
          <div key={s} className="bg-white border border-border px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{s.replace("_", " ")}</p>
            <p className="text-[16px] font-bold text-[var(--brand-primary)] mt-1 font-mono">{(cycle.review_counts || {})[s] || 0}</p>
          </div>
        ))}
      </div>

      {job && (
        <div className={`border px-4 py-3 ${jobRunning ? "bg-teal-50 border-teal-600/40" : "bg-white border-border"}`}
          data-testid="synthesis-job-status">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-800">
              Batch synthesis {job.status} — {job.done + job.failed} / {job.total}
              {job.failed > 0 && <span className="text-red-600 ml-2">{job.failed} failed</span>}
            </p>
            <p className="text-[10px] font-mono text-muted-foreground">started by {job.started_by}</p>
          </div>
          <div className="h-1.5 bg-white border border-teal-600/20">
            <div className="h-full bg-teal-600 transition-all"
              style={{ width: `${job.total ? ((job.done + job.failed) / job.total) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[820px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-slate-50">
              {["Employee", "Title", "Department", "Status", "Anchor", "Rating", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {cycle.reviews.map((r) => {
              const rating = r.final?.rating ?? r.draft?.suggested_rating;
              return (
                <tr key={r.id} data-testid={`review-row-${r.employee_id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-slate-800">{r.employee_name}</p>
                    <p className="text-[10px] font-mono text-slate-400">{r.employee_id}</p>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{r.title}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.department}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${REVIEW_STATUS_CHIP[r.status]}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-slate-500">{r.anchor_rating ?? "—"}</td>
                  <td className="px-4 py-2.5 font-mono font-semibold text-[var(--brand-primary)]">{rating ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.status === "pending" ? (
                      <Button size="sm" variant="outline" disabled={rowBusy === r.id || jobRunning}
                        data-testid={`synthesize-btn-${r.employee_id}`}
                        onClick={() => synthesizeOne(r)}
                        className="rounded-none h-7 text-[11px] border-teal-600/50 text-teal-700 hover:bg-teal-50">
                        <Sparkles className="h-3 w-3" /> {rowBusy === r.id ? "Working…" : "Synthesize"}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" data-testid={`view-review-btn-${r.employee_id}`}
                        onClick={() => setOpenReview(r.id)}
                        className="rounded-none h-7 text-[11px] text-slate-600">
                        {r.status === "finalized" ? "View" : "Review & finalize"} →
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openReview && (
        <ReviewDialog reviewId={openReview} onClose={() => { setOpenReview(null); load(); }} />
      )}
    </div>
  );
};

export default CycleDetail;
